import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { ScheduleItem } from '../SchedulerTypes';
import { ScheduleLogsTableProps } from './types';
import { getDoctorName, getStaffName, getRoomName } from './utils';
import SearchFilters from './SearchFilters';
import TableHeaderComponent from './TableHeader';
import ScheduleTableRow from './TableRow';
import NotesDialog from './NotesDialog';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

const ITEMS_PER_PAGE = 30;

const ScheduleLogsTable: React.FC<ScheduleLogsTableProps> = ({ 
  scheduleItems, 
  doctors, 
  staff, 
  rooms,
  onEditScheduleItem,
  onDeleteScheduleItem
}) => {
  const { toast } = useToast();
  const [viewingNotes, setViewingNotes] = useState<ScheduleItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPerson, setFilterPerson] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // Get unique schedule types for filter dropdown
  const scheduleTypes = useMemo(() => {
    const types = new Set<string>();
    scheduleItems.forEach(item => {
      if (item.type) types.add(item.type);
    });
    return Array.from(types);
  }, [scheduleItems]);

  // Get all people (doctors and staff) for filter dropdown
  const allPeople = useMemo(() => {
    const people: {id: string, name: string, type: 'doctor' | 'staff'}[] = [];
    
    doctors.forEach(doctor => {
      people.push({
        id: doctor.id,
        name: doctor.name,
        type: 'doctor'
      });
    });
    
    staff.forEach(staffMember => {
      people.push({
        id: staffMember.id,
        name: staffMember.name,
        type: 'staff'
      });
    });
    
    return people;
  }, [doctors, staff]);

  // Filter and sort the schedule items
  const filteredAndSortedItems = useMemo(() => {
    let filtered = [...scheduleItems];
    
    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const doctorName = item.doctorId ? getDoctorName(item.doctorId, doctors).toLowerCase() : '';
        const staffName = item.staffId ? getStaffName(item.staffId, staff).toLowerCase() : '';
        const roomName = item.roomId ? getRoomName(item.roomId, rooms).toLowerCase() : '';
        
        return (
          item.title?.toLowerCase().includes(lowerSearchTerm) ||
          doctorName.includes(lowerSearchTerm) ||
          staffName.includes(lowerSearchTerm) ||
          roomName.includes(lowerSearchTerm) ||
          item.notes?.toLowerCase().includes(lowerSearchTerm) ||
          item.type?.toLowerCase().includes(lowerSearchTerm)
        );
      });
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.type === filterType);
    }
    
    // Apply person filter
    if (filterPerson !== 'all') {
      const [personType, personId] = filterPerson.split(':');
      if (personType === 'doctor') {
        filtered = filtered.filter(item => item.doctorId === personId);
      } else if (personType === 'staff') {
        filtered = filtered.filter(item => item.staffId === personId);
      }
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let valueA;
      let valueB;
      
      switch (sortField) {
        case 'date':
          valueA = a.date;
          valueB = b.date;
          break;
        case 'time':
          valueA = a.startTime;
          valueB = b.startTime;
          break;
        case 'title':
          valueA = a.title || '';
          valueB = b.title || '';
          break;
        case 'type':
          valueA = a.type || '';
          valueB = b.type || '';
          break;
        default:
          valueA = a.date;
          valueB = b.date;
      }
      
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [scheduleItems, searchTerm, filterType, filterPerson, sortField, sortDirection, doctors, staff, rooms]);

  // Toggle sort direction when clicking on a column header
  const handleSortClick = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedItems.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedItems = filteredAndSortedItems.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, filterPerson]);

  // Export filtered data to CSV
  const handleExport = () => {
    try {
      const csvContent = generateCSV(filteredAndSortedItems);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `schedule_logs_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Export Successful',
        description: `Exported ${filteredAndSortedItems.length} schedule items to CSV`,
      });
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: 'There was an error exporting the data. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Generate CSV content from filtered items
  const generateCSV = (items: ScheduleItem[]) => {
    const headers = ['Date', 'Time', 'Title', 'Type', 'Doctor', 'Staff', 'Room', 'Notes'];
    const rows = items.map(item => [
      item.date,
      `${item.startTime} - ${item.endTime}`,
      item.title || '',
      item.type || '',
      item.doctorId ? getDoctorName(item.doctorId, doctors) : '',
      item.staffId ? getStaffName(item.staffId, staff) : '',
      item.roomId ? getRoomName(item.roomId, rooms) : '',
      item.notes ? item.notes.replace(/"/g, '""') : ''
    ]);

    const csvRows = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ];

    return csvRows.join('\n');
  };

  return (
    <div className="w-full space-y-4">
      {/* Search and Filter Controls */}
      <SearchFilters 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterType={filterType}
        setFilterType={setFilterType}
        filterPerson={filterPerson}
        setFilterPerson={setFilterPerson}
        scheduleTypes={scheduleTypes}
        allPeople={allPeople}
      />
      
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedItems.length)} of {filteredAndSortedItems.length} schedule items
        </div>
        <Button 
          onClick={handleExport}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          disabled={filteredAndSortedItems.length === 0}
          title={`Export ${filteredAndSortedItems.length} filtered items to CSV`}
        >
          <Download className="h-4 w-4" />
          Export to CSV ({filteredAndSortedItems.length})
        </Button>
      </div>
      
      <div className="w-full overflow-x-auto">
        <Table>
          <TableHeaderComponent 
            sortField={sortField}
            sortDirection={sortDirection}
            onSortClick={handleSortClick}
          />
          <TableBody>
            {filteredAndSortedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No schedule items found matching your filters
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((item) => (
                <ScheduleTableRow 
                  key={item.id}
                  item={item}
                  doctors={doctors}
                  staff={staff}
                  rooms={rooms}
                  onViewNotes={(item) => setViewingNotes(item)}
                  onEditScheduleItem={onEditScheduleItem}
                  onDeleteScheduleItem={onDeleteScheduleItem}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Previous</span>
          </Button>
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <Button
                  key={i}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  className="min-w-[40px]"
                >
                  {pageNum}
                </Button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="px-2">...</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  className="min-w-[40px]"
                >
                  {totalPages}
                </Button>
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4 sm:ml-1" />
          </Button>
        </div>
      )}

      {/* Notes Dialog */}
      <NotesDialog 
        viewingNotes={viewingNotes} 
        onClose={() => setViewingNotes(null)} 
      />
    </div>
  );
};

export default ScheduleLogsTable;
